import React, { useState } from "react";
import CommonModal from "../../components/modals/CommonModal";
import {
  Grid,
  Box,
  TextField,
  Typography,
  Stack,
  MenuItem,
  Divider,
} from "@mui/material";
import {
  createTariffSlabV2,
  updateTariffSlabV2,
  CreateTariffSlabV2Request,
  TariffSlabV2,
} from "../../utils/tariffServiceV2";
import { useEffect } from "react";

import CustomToast from "../../components/toast/CustomToast";
import { ToastState } from "../../types";
import { Clock, Calculator, Tag } from "lucide-react";

interface AddSlabModalProps {
  open: boolean;
  onClose: () => void;
  tariffId: string;
  onSuccess: () => void;
  editData?: TariffSlabV2 | null;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

const AddSlabModal: React.FC<AddSlabModalProps> = ({
  open,
  onClose,
  tariffId,
  onSuccess,
  editData,
}) => {
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({ message: "", severity: "" });
  const [formData, setFormData] = useState<CreateTariffSlabV2Request>({
    slabName: "",
    slabType: "",
    minUnits: 0,
    maxUnits: 0,
    ratePerUnit: 0,
    fixedCharge: 0,
    timeOfUseStart: { hour: 0, minute: 0 },
    timeOfUseEnd: { hour: 23, minute: 59 },
    isActive: true, // Based on API curl
  } as any);

  const parseTime = (time: any) => {
    if (!time) return { hour: 0, minute: 0 };
    if (typeof time === "object")
      return { hour: time.hour ?? 0, minute: time.minute ?? 0 };
    if (typeof time === "string" && time.includes(":")) {
      const [h, m] = time.split(":").map(Number);
      return { hour: isNaN(h) ? 0 : h, minute: isNaN(m) ? 0 : m };
    }
    return { hour: 0, minute: 0 };
  };

  useEffect(() => {
    if (editData && open) {
      setFormData({
        slabName: editData.slabName || "",
        slabType: editData.slabType || "",
        minUnits: editData.minUnits ?? 0,
        maxUnits: editData.maxUnits ?? 0,
        ratePerUnit: editData.ratePerUnit ?? 0,
        fixedCharge: editData.fixedCharge ?? 0,
        timeOfUseStart: parseTime(editData.timeOfUseStart),
        timeOfUseEnd: parseTime(editData.timeOfUseEnd),
        isActive: editData.isActive ?? true,
      } as any);
    } else if (!editData && open) {
      setFormData({
        slabName: "",
        slabType: "",
        minUnits: 0,
        maxUnits: 0,
        ratePerUnit: 0,
        fixedCharge: 0,
        timeOfUseStart: { hour: 0, minute: 0 },
        timeOfUseEnd: { hour: 23, minute: 59 },
        isActive: true,
      } as any);
    }
  }, [editData, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: ["minUnits", "maxUnits", "ratePerUnit", "fixedCharge"].includes(
        name,
      )
        ? Number(value)
        : value,
    }));
  };

  const handleTimeChange = (
    type: "start" | "end",
    field: "hour" | "minute",
    value: number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [type === "start" ? "timeOfUseStart" : "timeOfUseEnd"]: {
        ...prev[type === "start" ? "timeOfUseStart" : "timeOfUseEnd"],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!formData.slabName) {
      setToast({ message: "Slab Name is required", severity: "warning" });
      return;
    }

    try {
      setLoading(true);
      let response;
      if (editData) {
        response = await updateTariffSlabV2(tariffId, editData.id, formData);
      } else {
        response = await createTariffSlabV2(tariffId, formData);
      }

      if (response && response.success) {
        setToast({
          message: editData
            ? "Slab updated successfully"
            : "Slab added successfully",
          severity: "success",
        });
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      } else {
        setToast({
          message:
            response?.message ||
            `Failed to ${editData ? "update" : "add"} slab`,
          severity: "error",
        });
      }
    } catch (error) {
      console.error(`Error ${editData ? "updating" : "adding"} slab:`, error);
      setToast({ message: "An error occurred", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const body = (
    <Box sx={{ py: 1 }}>
      {/* Flat Metadata Section */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
        <Box sx={{ color: "#1064ED", display: "flex", p: 0.5 }}>
          <Tag size={18} />
        </Box>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 800, color: "#1E293B" }}
        >
          SLAB BASIC INFO
        </Typography>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="SLAB NAME"
            name="slabName"
            placeholder="e.g., Residential Peak"
            value={formData.slabName}
            onChange={handleChange}
            variant="outlined"
            size="small"
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: "10px", fontWeight: 700, color: "#64748B" },
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="SLAB TYPE"
            name="slabType"
            value={formData.slabType}
            onChange={handleChange}
            variant="outlined"
            size="small"
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: "10px", fontWeight: 700, color: "#64748B" },
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
          >
            {[
              "FlatRate",
              "FixedCharge",
              "TieredSlab",
              "PeakHours",
              "OffPeakHours",
              "TamperCharge",
            ].map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 1, mt: 1.5 }}
          >
            <Box sx={{ color: "#1064ED" }}>
              <Calculator size={18} />
            </Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, color: "#1E293B" }}
            >
              RATES & LIMITS
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="MIN UNITS"
            name="minUnits"
            type="number"
            value={formData.minUnits}
            onChange={handleChange}
            variant="outlined"
            size="small"
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: "10px", fontWeight: 700, color: "#64748B" },
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="MAX UNITS"
            name="maxUnits"
            type="number"
            value={formData.maxUnits}
            onChange={handleChange}
            variant="outlined"
            size="small"
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: "10px", fontWeight: 700, color: "#64748B" },
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="RATE PER UNIT (₹)"
            name="ratePerUnit"
            type="number"
            value={formData.ratePerUnit}
            onChange={handleChange}
            variant="outlined"
            size="small"
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: "10px", fontWeight: 700, color: "#64748B" },
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="FIXED CHARGE (₹)"
            name="fixedCharge"
            type="number"
            value={formData.fixedCharge}
            onChange={handleChange}
            variant="outlined"
            size="small"
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: "10px", fontWeight: 700, color: "#64748B" },
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2, borderColor: "#464A4F26" }} />
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Box sx={{ color: "#1064ED" }}>
              <Clock size={18} />
            </Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, color: "#1E293B" }}
            >
              TIME OF USE (TOU)
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={6}>
          <Typography
            variant="caption"
            color="#64748B"
            sx={{ fontWeight: 700, fontSize: "9px", mb: 1, display: "block" }}
          >
            START TIME
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField
              select
              value={formData.timeOfUseStart.hour}
              onChange={(e) =>
                handleTimeChange("start", "hour", Number(e.target.value))
              }
              size="small"
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": { borderRadius: "6px" },
              }}
            >
              {HOURS.map((h) => (
                <MenuItem key={h} value={h}>
                  {String(h).padStart(2, "0")}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              value={formData.timeOfUseStart.minute}
              onChange={(e) =>
                handleTimeChange("start", "minute", Number(e.target.value))
              }
              size="small"
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": { borderRadius: "6px" },
              }}
            >
              {MINUTES.map((m) => (
                <MenuItem key={m} value={m}>
                  {String(m).padStart(2, "0")}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Grid>

        <Grid item xs={6}>
          <Typography
            variant="caption"
            color="#64748B"
            sx={{ fontWeight: 700, fontSize: "9px", mb: 1, display: "block" }}
          >
            END TIME
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField
              select
              value={formData.timeOfUseEnd.hour}
              onChange={(e) =>
                handleTimeChange("end", "hour", Number(e.target.value))
              }
              size="small"
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": { borderRadius: "6px" },
              }}
            >
              {HOURS.map((h) => (
                <MenuItem key={h} value={h}>
                  {String(h).padStart(2, "0")}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              value={formData.timeOfUseEnd.minute}
              onChange={(e) =>
                handleTimeChange("end", "minute", Number(e.target.value))
              }
              size="small"
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": { borderRadius: "6px" },
              }}
            >
              {MINUTES.map((m) => (
                <MenuItem key={m} value={m}>
                  {String(m).padStart(2, "0")}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Grid>
      </Grid>

      <CustomToast
        open={toast.message}
        severity={toast.severity as any}
        setToast={setToast}
      />
    </Box>
  );

  return (
    <CommonModal
      title={editData ? "Edit Tariff Slab" : "Add Tariff Slab"}
      open={open}
      onClose={onClose}
      body={body}
      handleSave={handleSave}
      showEditButton
      loading={loading}
      maxWidth="sm"
      fullWidth
    />
  );
};

export default AddSlabModal;
